import { ref, watch, computed } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { TABLE } from '@/constants/config';
import { pluck, formatDate, generateRandomUniqueId } from '@/helpers';
import CompanySecretary from '@/models/CompanySecretary';
import changeSecretariesFormLocalizer from '@/localize/changeSecretariesFormLocalizer';

export default function useSecretariesTable() {
    const toast = useToast();
    const refSecretaryListTable = ref(null);
    const secretaries = ref(null);
    const changeSecretaryFormData = ref([]);

    // Table Handlers
    const tableColumns = [
        { key: 'index', label: '#', thClass: 'px-1 pr-2', tdClass: 'px-1 pr-2' },
        { key: 'nric', label: 'ID', sortable: true, thClass: 'px-0 pr-2', tdClass: 'px-0 pr-2' },
        { key: 'name', label: 'NAME', sortable: true, thClass: 'px-0 pr-2', tdClass: 'px-0 pr-2' },
        { key: 'email', label: 'EMAIL', sortable: true, thClass: 'px-0 pr-2', tdClass: 'px-0 pr-2 text-break' },
        { key: 'appointmentDate', label: 'APPOINTMENT', sortable: true, thClass: 'px-0 pr-2', tdClass: 'px-0 pr-2' },
        { key: 'cessationDate', label: 'CESSATION', sortable: true, thClass: 'px-0 pr-2', tdClass: 'px-0 pr-2' },
        { key: 'actions', label: 'ACTION', thClass: 'pl-0 pr-1 text-right', tdClass: 'pl-0 pr-1 text-right' },
    ];

    const perPage = ref(TABLE.PER_PAGE);
    const totalSecretaries = ref(0);
    const currentPage = ref(1);
    const perPageOptions = TABLE.PER_PAGE_OPTIONS;
    const searchQuery = ref('');
    const sortBy = ref('id');
    const isSortDirDesc = ref(true);
    const isBusy = ref(false);

    // local variable to store the companies list
    const localSecretaries = ref([]);

    const isLoading = ref(false);

    // options where the search query is applied
    // make sure to list as keys of how to access those values in table 
    const filterOn = ref([
        'nric',
        'name',
        'email',
    ]);

    const dataMeta = computed(() => {
        const localItemsCount = refSecretaryListTable.value ? refSecretaryListTable.value.localItems.length : 0;
        totalSecretaries.value = localItemsCount;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalSecretaries.value,
        };
    });
   
    // to change api data to straightforward easy, js, local data
    const localizeData = data => ({
        _id: generateRandomUniqueId(),
        id: data.id,
        userId: data.user?.id,
        idType: data.user.user_info?.id_type
            ? {
                id: data.user.user_info.id_type?.id,
                name: data.user.user_info.id_type?.name,
            }
            : undefined,
        nric: data.user?.user_info?.NRIC,
        name: data.user?.user_info?.legal_name ?? data.user?.name,
        dob: formatDate(data.user?.user_info?.DOB),
        dateOfExpiry: formatDate(data.user?.user_info?.date_of_expiry),
        nationality: {
            text: data.user?.user_info?.nationality?.name,
            value: data.user?.user_info?.nationality?.id
        },
        email: data.user?.email,
        contactNumber: data.user?.user_info?.contact_number,
        address: data.user?.user_info?.address,
        appointmentDate: formatDate(data.appointment_date),
        cessationDate: formatDate(data.cessation_date),
    });

    const fetchSecretaries = () => {
        isLoading.value = true;

        CompanySecretary.all().then(data => {
            localSecretaries.value = data.secretaries?.map(secretary => localizeData(secretary)) ?? [];
            
            secretaries.value = localSecretaries.value;

            // secretary change secretary data
            changeSecretaryFormData.value = data.pending_approval_secretaries?.data.map(
                changeSecretaryForm => changeSecretariesFormLocalizer(changeSecretaryForm)
            ) ?? [];
    
            isLoading.value = false;
        })
            .catch(e => {
                console.error("Error fetching secretaries", e);

                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Error fetching secretary list',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                });
            });
    };

    fetchSecretaries();

    const refetchData = () => {
        refSecretaryListTable.value.refresh();
    };

    watch([currentPage, perPage, searchQuery], () => {
        refetchData();
        
        let data = localSecretaries.value;
        const queryLowered = searchQuery.value.toLowerCase().trim();

        //  if a query present then search
        if (queryLowered)
        {
            data = localSecretaries.value.filter(item => filterOn.value.some(key => {
                const value = pluck(key, item);
                return value && value.toString().toLowerCase().trim().includes(queryLowered);
            }));
        }

        refSecretaryListTable.value.localItems = data;
        isBusy.value = false;
    });

    return {
        sortBy,
        isBusy,
        perPage,
        dataMeta,
        isLoading,
        searchQuery,
        currentPage,
        tableColumns,
        isSortDirDesc,
        totalSecretaries,
        perPageOptions,
        refSecretaryListTable,

        secretaries,
        changeSecretaryFormData,
        refetchData,
        fetchSecretaries,
        filterOn,
        localizeData,
    };
}
